/**
 * Browser-specific provider-account patches and status shapes, free of React.
 * Wholesale save projection, list upsert and account-id collection belong to the import-free
 * `./providerAccounts` mirror of `src/shared/providerAccounts.ts`.
 */
import {
  PROVIDER_ROUTING_DEFAULTS, upsertProvider as upsertProviderList,
  type BrainProviderConnectionView, type ConnectionPatch, type ProviderEdit, type ProviderRoutingConfig,
} from './providerAccounts';
export { stripProvider, takenConnectionIds } from './providerAccounts';
export type { ConnectionPatch } from './providerAccounts';
import type { BrainNativeProvider, BrainProvider } from './types';
import type { BrainRouterConfig } from '../../src/shared/providerAccounts';

/** One sign-in and the whole status answer, as `GET /brain/oauth/status` reports them: the daemon's own
 *  declarations, consumed as erased types from their one owner. Availability is asked per CONNECTION, never
 *  per provider type: one type may carry several accounts, and only the connection says which of them holds
 *  a usable login. */
import type { BrainOAuthConnectionStatus, BrainOAuthStatusResponse } from '../../src/shared/oauthConnections';
export type { BrainOAuthConnectionStatus, BrainOAuthStatusResponse };

/** The web's public provider shape projected to the shared config PUT account form. */
export type PersistedProvider = ProviderEdit<BrainProvider>;

/** Replace in place or append once: the save is a wholesale list, so an edit is one mapped entry. */
export function upsertProvider(providers: readonly BrainProvider[], entry: PersistedProvider): PersistedProvider[] {
  return upsertProviderList(providers, entry, 'replace');
}

/** Edit one account of a provider, leaving every other account and field exactly as it was. */
export function patchConnection(entry: PersistedProvider, connectionId: string, patch: Partial<ConnectionPatch>): PersistedProvider {
  if (entry.type === 'router') throw new Error('A router has no owned accounts');
  return { ...entry, connections: entry.connections.map((connection) => connection.id === connectionId ? { ...connection, ...patch } : connection) };
}

/** Add an account to a provider. */
export function addConnection(entry: PersistedProvider, connection: ConnectionPatch): PersistedProvider {
  if (entry.type === 'router') throw new Error('A router has no owned accounts');
  return { ...entry, connections: [...entry.connections, connection] };
}

/** Remove one account. A routing default that named it is cleared rather than left dangling: the operator
 *  chose that account on purpose, so nothing silently promotes another one. */
export function removeConnection(entry: PersistedProvider, connectionId: string): PersistedProvider {
  if (entry.type === 'router') throw new Error('A router has no owned accounts');
  const connections = entry.connections.filter((connection) => connection.id !== connectionId);
  return entry.routing.defaultConnectionId === connectionId
    ? { ...entry, connections, routing: { ...entry.routing, defaultConnectionId: null } }
    : { ...entry, connections };
}

/** Merge a routing change onto the provider's policy. A default naming an account this provider does not
 *  own is refused by the store, so the dialog can only offer the provider's own accounts. */
export function patchRouting(entry: PersistedProvider, patch: Partial<ProviderRoutingConfig>): PersistedProvider {
  return { ...entry, routing: { ...entry.routing, ...patch } };
}

/** A new account of an existing provider: served right away, open to everyone the provider is granted to. */
export function newConnection(id: string, label: string): ConnectionPatch {
  return { id, label, enabled: true, generation: 1, spendLimitMicrousd: null };
}

/** A new provider entry with its first account. Routing starts off — the migrated default — with THIS
 *  account named as the default, so routing-off has one explicit choice instead of an implicit pick. */
export function newProviderEntry(input: {
  id: string; label: string; type: BrainNativeProvider['type']; baseUrl: string; connection: ConnectionPatch;
}): ProviderEdit<BrainNativeProvider> {
  return {
    id: input.id,
    label: input.label,
    type: input.type,
    baseUrl: input.baseUrl,
    models: [],
    connections: [input.connection],
    routing: { ...PROVIDER_ROUTING_DEFAULTS, defaultConnectionId: input.connection.id },
  };
}

/** A router being composed in the drawer. It refers to real accounts and owns no endpoint or credentials; with
 *  no member yet it is not a valid stored router until it has two accounts of two providers. Routing starts on. */
export function newRouterDraft(id: string, label: string): BrainRouterConfig {
  return { id, label, type: 'router', connectionIds: [], models: [], connections: [],
    routing: { ...PROVIDER_ROUTING_DEFAULTS, enabled: true, defaultConnectionId: null } };
}

/** One account of a native provider, as a router names it. */
export interface RouterMember { provider: BrainNativeProvider; connection: BrainProviderConnectionView }

/** The router's member accounts in its own order. Deleting a referenced account is refused, so every id
 *  resolves; an id that does not is left out rather than invented. */
export function routerMembers(providers: readonly BrainNativeProvider[], connectionIds: readonly string[]): RouterMember[] {
  return connectionIds.flatMap((id) => {
    for (const provider of providers) {
      const connection = provider.connections.find((entry) => entry.id === id);
      if (connection) return [{ provider, connection }];
    }
    return [];
  });
}

/** A router needs at least two accounts of at least two different providers (the daemon refuses less). */
export function routerMembersValid(members: readonly RouterMember[]): boolean {
  return members.length >= 2 && new Set(members.map(({ provider }) => provider.id)).size >= 2;
}

/** Every account of every provider that the router does not already use, in provider order. A provider
 *  already in the router stays offered: a router needs two providers, not one account per provider. */
export function routerCandidates(providers: readonly BrainNativeProvider[], connectionIds: readonly string[]): RouterMember[] {
  return providers.flatMap((provider) => provider.connections
    .filter((connection) => !connectionIds.includes(connection.id))
    .map((connection) => ({ provider, connection })));
}

/** Add one account to a router. */
export function addRouterMember(entry: PersistedProvider, connectionId: string): PersistedProvider {
  if (entry.type !== 'router') throw new Error('Only a router has member accounts');
  return { ...entry, connectionIds: [...entry.connectionIds, connectionId] };
}

/** Remove one account from a router. A routing default that named it is cleared, as `removeConnection` does. */
export function removeRouterMember(entry: PersistedProvider, connectionId: string): PersistedProvider {
  if (entry.type !== 'router') throw new Error('Only a router has member accounts');
  const connectionIds = entry.connectionIds.filter((id) => id !== connectionId);
  return entry.routing.defaultConnectionId === connectionId
    ? { ...entry, connectionIds, routing: { ...entry.routing, defaultConnectionId: null } }
    : { ...entry, connectionIds };
}

/** The sign-in the daemon reported for one account, if any. */
export function connectionLogin(status: BrainOAuthStatusResponse | undefined, connectionId: string): BrainOAuthConnectionStatus | undefined {
  return status?.connections.find((connection) => connection.connectionId === connectionId);
}

/** What an account's row states, from its own switch and its credential. A paused account is neither
 *  connected nor missing: it keeps its login and simply receives no new work. */
export type AccountState = 'paused' | 'missing' | 'connected';
export function accountState(connection: BrainProviderConnectionView, login: BrainOAuthConnectionStatus | undefined): AccountState {
  if (!connection.enabled) return 'paused';
  return connection.apiKeySet || login?.connected ? 'connected' : 'missing';
}
